import deepEqual from "fast-deep-equal";
import { atom } from "jotai";
import { atomFamily } from "jotai/utils";
import { Page, ZSLInterface } from "../types";
import { zslAtom } from "../templates";
import { isTemplateInterfaceProjectId } from "../utils";
import { interfaceProjectAsyncAtomFamily } from "./interfaceProjectAsyncAtomFamily";
import { aiSuggestedEditsAtomFamily } from "../nodes";

export const interfaceProjectPagesAsyncAtomFamily = atomFamily(
  ({
    interfaceProjectId,
    proposalId,
    nodeId,
  }: {
    interfaceProjectId: string | undefined;
    proposalId: string | undefined;
    nodeId: string;
  }) => {
    const interfaceProjectPagesAtom = atom(async (get) => {
      if (!interfaceProjectId) {
        return null;
      }

      const isTemplateInterface =
        isTemplateInterfaceProjectId(interfaceProjectId);

      // Handle all template interface logic in one place
      if (isTemplateInterface) {
        const aiProposedEdits = get(aiSuggestedEditsAtomFamily(nodeId));
        const zsl = get(zslAtom);

        if (aiProposedEdits && proposalId) {
          // Check for proposal-specific edits first
          const aiProposed = aiProposedEdits.additions.find(
            (addition) => addition.id === proposalId,
          );

          if (aiProposed) {
            if (aiProposed.zsl?.interfaces) {
              return aiProposed.zsl.interfaces[0].pages;
            }
            if (aiProposed.outline?.interfaces[0]?.interfaces[0]?.pages) {
              return aiProposed.outline.interfaces[0].interfaces[0].pages;
            }
          }
        }

        if (zsl) {
          const templateInterface = (await get(
            interfaceProjectAsyncAtomFamily({
              interfaceProjectId,
              proposalId,
              nodeId,
            }),
          )) as ZSLInterface;

          if (templateInterface) {
            return templateInterface.pages;
          }
        }

        return null;
      }

      // Handle non-template interfaces
      try {
        const res = await fetch(
          `/api/canvasui/interfaces/${interfaceProjectId}/pages`,
        );

        if (res.ok) {
          const {
            result: { data },
          } = await res.json();

          return data as Page[];
        } else {
          console.error("Failed to fetch interface pages:", res.statusText);
          return null;
        }
      } catch (error) {
        console.error("Error fetching interface pages:", error);
        return null;
      }
    });

    interfaceProjectPagesAtom.debugLabel = `interfaceProjectPagesAsyncAtomFamily(${interfaceProjectId})-nodeId:${nodeId})`;
    return interfaceProjectPagesAtom;
  },
  deepEqual,
);
